import { useEffect, useMemo, useState } from "react";
import type React from "react";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Bell,
  Bot,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Database,
  FileText,
  FolderOpen,
  Grid3X3,
  LayoutDashboard,
  Link2,
  MessageSquare,
  Monitor,
  Plus,
  ReceiptText,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import type { DemoData } from "@/src/data/demos";
import { cn } from "@/src/lib/utils";
import {
  buildDemoShellAgents,
  buildDemoPermissionReceipt,
  buildDemoRecordActionReceipt,
  buildDemoShellReceipts,
  buildDemoShellRecords,
  buildDemoShellScreens,
  singularDemoNoun,
  splitDemoStages,
  titleCase,
  type DemoShellReceipt,
  type DemoShellRecord,
} from "@/src/lib/agentic-demo-shell";

const STANDARD_SHELL_SCREENS = ["Agent Library", "Integrations", "Decisions", "Receipts", "Weekly Review"];

const CATEGORY_ACCENTS: Record<string, string> = {
  Sports: "#ff3d67",
  "Real Estate": "#5b6cff",
  Marketing: "#e949ff",
  Energy: "#f59e0b",
  Ecommerce: "#38bdf8",
  Recruiting: "#a78bfa",
  "Field Service": "#fb923c",
  Legal: "#cbd5e1",
  Healthcare: "#22d3ee",
  Construction: "#facc15",
  Hospitality: "#f87171",
  Nonprofit: "#34d399",
  Insurance: "#60a5fa",
  Logistics: "#818cf8",
  Wellness: "#a3e635",
  "Customer Success": "#2dd4bf",
  Manufacturing: "#d4d4d8",
  Automotive: "#fb7185",
  Education: "#c084fc",
  Finance: "#4ade80",
  Trade: "#fbbf24",
  Design: "#d6b17c",
  Academy: "#fda4af",
  Security: "#a3a3a3",
  Events: "#f472b6",
};

function demoApplicationName(demo: DemoData): string {
  return demo.name
    .replace(/\s*Agentic Operating System \(Application\)$/i, " Agentic Operating System")
    .replace(/\s*Agentic Operating System$/i, " Agentic Operating System");
}

function demoWorkspaceName(demo: DemoData): string {
  return demoApplicationName(demo).replace(/\s*Agentic Application$/i, "");
}

function accentFor(demo: DemoData): string {
  return CATEGORY_ACCENTS[demo.category] || "#4ade80";
}

function screenIcon(screen: string) {
  const lower = screen.toLowerCase();
  if (lower.includes("library")) return Bot;
  if (lower.includes("mission") || lower.includes("dashboard") || lower.includes("pulse")) return LayoutDashboard;
  if (lower.includes("weekly review")) return ClipboardList;
  if (lower.includes("calendar") || lower.includes("schedule")) return Activity;
  if (lower.includes("document") || lower.includes("vault") || lower.includes("receipt")) return FileText;
  if (lower.includes("integration")) return Link2;
  if (lower.includes("decision") || lower.includes("approval")) return ShieldCheck;
  if (lower.includes("analytics") || lower.includes("performance") || lower.includes("revenue")) return BarChart3;
  if (lower.includes("agent") || lower.includes("assistant")) return Bot;
  return Grid3X3;
}

function screenDescription(screen: string, demo: DemoData) {
  const noun = demo.vocabulary.tracks;
  const lower = screen.toLowerCase();

  if (lower.includes("mission")) return "Daily command center for Decisions, agent messages, receipts, and upgrade notices.";
  if (lower.includes("agent library")) return `Active and recommended agents for ${demo.category.toLowerCase()}, with every deployment routed through Decisions.`;
  if (lower.includes("weekly review")) return `A recurring review surface where agents summarize movement, stalled ${noun}, missing data, and suggested application upgrades.`;
  if (lower.includes("integration")) return `Shows every approved source the agents can use for ${noun}.`;
  if (lower.includes("decision") || lower.includes("approval")) return "Sensitive changes land here before any agent deploys or changes the application.";
  if (lower.includes("receipt")) return "Every completed agent move is logged so the user knows what happened.";
  if (lower.includes("calendar") || lower.includes("schedule")) return `Time-based view for ${noun}, deadlines, handoffs, and follow-ups.`;
  if (lower.includes("document") || lower.includes("vault")) return `Files, drafts, evidence, and outputs attached to the right ${singularDemoNoun(noun)}.`;
  if (lower.includes("analytics") || lower.includes("dashboard") || lower.includes("performance")) return `Charts, rankings, gaps, and next-best actions from live ${noun} data.`;
  if (lower.includes("assistant") || lower.includes("agent")) return "Agent workspace with skills, data needs, outputs, and failed-run recovery.";
  return `Operational screen for moving ${noun} through ${demo.vocabulary.stages}.`;
}

export default function AgenticOSDemoShell({ demo, embedded = false }: { demo: DemoData; embedded?: boolean }) {
  const navigate = useNavigate();
  const accent = accentFor(demo);
  const screens = useMemo(() => buildDemoShellScreens(demo), [demo]);
  const agents = useMemo(() => buildDemoShellAgents(demo), [demo]);
  const records = useMemo(() => buildDemoShellRecords(demo), [demo]);
  const stages = useMemo(() => splitDemoStages(demo), [demo]);
  const workspaceScreens = useMemo(() => screens.filter((screen) => !STANDARD_SHELL_SCREENS.includes(screen)), [screens]);
  const systemScreens = useMemo(() => STANDARD_SHELL_SCREENS.filter((screen) => screens.includes(screen)), [screens]);
  const [activeScreen, setActiveScreen] = useState("Mission Control");
  const [selectedRecord, setSelectedRecord] = useState<DemoShellRecord | null>(records[0] || null);
  const [desktopPermissionGranted, setDesktopPermissionGranted] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState("data_access");
  const [receipts, setReceipts] = useState<DemoShellReceipt[]>(() => buildDemoShellReceipts(demo, records));

  const applicationName = demoApplicationName(demo);
  const workspaceName = demoWorkspaceName(demo);
  const noun = demo.vocabulary.tracks;
  const singularNoun = titleCase(singularDemoNoun(noun));

  useEffect(() => {
    setActiveScreen("Mission Control");
    setSelectedRecord(records[0] || null);
    setDesktopPermissionGranted(false);
    setSelectedDecision("data_access");
    setReceipts(buildDemoShellReceipts(demo, records));
  }, [demo, records]);

  const addReceipt = (receipt: DemoShellReceipt) => {
    setReceipts((current) => current.some((item) => item.id === receipt.id) ? current : [receipt, ...current]);
  };

  const grantDesktopPermission = () => {
    setDesktopPermissionGranted(true);
    addReceipt(buildDemoPermissionReceipt(demo));
    setActiveScreen("Integrations");
  };

  const takeRecordAction = (record: DemoShellRecord) => {
    addReceipt(buildDemoRecordActionReceipt(demo, record));
    setSelectedRecord(record);
    setActiveScreen("Receipts");
  };

  return (
    <div className="min-h-screen bg-[#080808] text-[#f4f4f5] font-sans">
      <div
        className="h-9 border-b border-white/10 flex items-center justify-center text-[11px] font-semibold tracking-wide text-[#0d0d0d]"
        style={{ backgroundColor: accent }}
      >
        {applicationName} - Sloe live Agentic Operating System demo
      </div>
      <div className="flex min-h-[calc(100vh-36px)]">
        <aside className="hidden lg:flex w-[276px] shrink-0 border-r border-white/10 bg-[#101010] flex-col">
          <div className="h-[74px] border-b border-white/10 px-5 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center font-bold text-[#090909]" style={{ backgroundColor: accent }}>
              {workspaceName.slice(0, 1)}
            </div>
            <div className="min-w-0">
              <div className="font-semibold truncate">{workspaceName}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{demo.category}</div>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto p-3">
            <SectionLabel>Workspace</SectionLabel>
            {workspaceScreens.map((screen) => (
              <ShellNavButton
                key={screen}
                label={screen}
                active={activeScreen === screen}
                accent={accent}
                onClick={() => setActiveScreen(screen)}
              />
            ))}

            <SectionLabel className="mt-6">Agents</SectionLabel>
            {agents.slice(0, 3).map((agent) => (
              <ShellNavButton
                key={agent.name}
                label={agent.name}
                icon={Bot}
                active={activeScreen === agent.name}
                accent={accent}
                onClick={() => setActiveScreen(agent.name)}
              />
            ))}

            <SectionLabel className="mt-6">System</SectionLabel>
            {systemScreens.map((screen) => (
              <ShellNavButton
                key={screen}
                label={screen}
                active={activeScreen === screen}
                accent={accent}
                onClick={() => setActiveScreen(screen)}
              />
            ))}
          </nav>

          <div className="border-t border-white/10 p-3">
            <button
              className="w-full rounded-lg border border-white/10 px-3 py-3 text-left text-sm text-zinc-300 hover:bg-white/5 transition-colors"
              onClick={() => setActiveScreen("Integrations")}
            >
              <div className="flex items-center gap-2 font-semibold">
                <Monitor className="h-4 w-4" />
                Desktop Agent
              </div>
              <div className="mt-1 text-xs text-zinc-500">Demo mode: permission path visible.</div>
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <header className="h-[74px] border-b border-white/10 bg-[#0d0d0d] px-5 sm:px-8 flex items-center justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                {!embedded && (
                  <button onClick={() => navigate(-1)} className="hover:text-zinc-200 transition-colors" aria-label="Back">
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
                <span className="truncate">{workspaceName}</span>
                <span>/</span>
                <span className="font-semibold text-zinc-200 truncate">{activeScreen}</span>
              </div>
              <div className="mt-1 text-xs text-zinc-500 truncate">
                Tracks {noun} with {agents.length} agents and {screens.length} screens.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="hidden sm:inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm font-semibold text-zinc-200 hover:bg-white/[0.07]"
                onClick={() => setActiveScreen("The Operator")}
              >
                <Sparkles className="h-4 w-4" style={{ color: accent }} />
                Ask Operator
              </button>
              <Link
                to="/build"
                state={{ demo }}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-[#090909]"
                style={{ backgroundColor: accent }}
              >
                Build Like This
              </Link>
            </div>
          </header>

          <div className="px-5 py-7 sm:px-8 lg:px-12 lg:py-10">
            {activeScreen === "Mission Control" ? (
              <MissionControlView
                demo={demo}
                accent={accent}
                agents={agents}
                records={records}
                desktopPermissionGranted={desktopPermissionGranted}
                onOpenScreen={setActiveScreen}
              />
            ) : activeScreen === "Integrations" ? (
              <IntegrationsView
                demo={demo}
                accent={accent}
                permissionGranted={desktopPermissionGranted}
                onGrantPermission={grantDesktopPermission}
                onOpenScreen={setActiveScreen}
              />
            ) : activeScreen === "Decisions" ? (
              <DecisionsView
                demo={demo}
                accent={accent}
                selectedDecision={selectedDecision}
                setSelectedDecision={setSelectedDecision}
                onGrantPermission={grantDesktopPermission}
              />
            ) : activeScreen === "Receipts" ? (
              <ReceiptsView demo={demo} accent={accent} receipts={receipts} />
            ) : activeScreen === "Agent Library" ? (
              <AgentLibraryView demo={demo} accent={accent} agents={agents} onOpenScreen={setActiveScreen} />
            ) : activeScreen === "Weekly Review" ? (
              <WeeklyReviewView demo={demo} accent={accent} records={records} onOpenScreen={setActiveScreen} />
            ) : agents.some((agent) => agent.name === activeScreen) ? (
              <AgentWorkspaceView
                demo={demo}
                accent={accent}
                agentName={activeScreen}
                onOpenScreen={setActiveScreen}
              />
            ) : (
              <WorkspaceScreenView
                demo={demo}
                accent={accent}
                activeScreen={activeScreen}
                stages={stages}
                records={records}
                selectedRecord={selectedRecord}
                setSelectedRecord={setSelectedRecord}
                onTakeAction={takeRecordAction}
                singularNoun={singularNoun}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500", className)}>{children}</div>;
}

function ShellNavButton({
  label,
  active,
  accent,
  onClick,
  icon,
}: {
  label: string;
  active: boolean;
  accent: string;
  onClick: () => void;
  icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
}) {
  const Icon = icon || screenIcon(label);

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
        active ? "bg-white/[0.11] text-white" : "text-zinc-500 hover:bg-white/[0.06] hover:text-zinc-200"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" style={active ? { color: accent } : undefined} />
      <span className="truncate">{label}</span>
    </button>
  );
}

function MissionControlView({
  demo,
  accent,
  agents,
  records,
  desktopPermissionGranted,
  onOpenScreen,
}: {
  demo: DemoData;
  accent: string;
  agents: ReturnType<typeof buildDemoShellAgents>;
  records: DemoShellRecord[];
  desktopPermissionGranted: boolean;
  onOpenScreen: (screen: string) => void;
}) {
  const workspaceName = demoWorkspaceName(demo);
  const stages = splitDemoStages(demo);
  const recommendedAgents = agents.filter((agent) => agent.status === "Recommended");
  const receiptsReady = records.length > 0;
  const checklist = [
    {
      title: desktopPermissionGranted ? "Data permission active" : "Data permission needed",
      description: desktopPermissionGranted
        ? `The demo source for ${demo.vocabulary.tracks} is approved and visible in Integrations.`
        : `Review how agents ask where ${demo.vocabulary.tracks} live before reading anything.`,
      action: "Review action",
      screen: "Integrations",
      ok: desktopPermissionGranted,
    },
    {
      title: "Workspace screens ready",
      description: `${workspaceName} opens with ${demo.vocabulary.tracks}, stage views, agent workspaces, Decisions, and Receipts.`,
      action: "Open board",
      screen: buildDemoShellScreens(demo).find((screen) => !STANDARD_SHELL_SCREENS.includes(screen)) || "Weekly Review",
      ok: true,
    },
    {
      title: "Agent actions gated",
      description: "Sensitive source access, specialist deployment, and screen changes route through Decisions first.",
      action: "Open Decisions",
      screen: "Decisions",
      ok: true,
    },
    {
      title: receiptsReady ? "Receipts available" : "Receipts ready",
      description: receiptsReady
        ? "Demo receipts show how completed agent work is explained after an action."
        : "Every move an agent takes lands in Receipts once work starts.",
      action: "View receipts",
      screen: "Receipts",
      ok: receiptsReady,
    },
  ];

  return (
    <div className="space-y-10">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Good afternoon, {workspaceName}</h1>
          <p className="mt-3 max-w-3xl text-zinc-500">
            This is the shared Agentic Application shell adapted for {demo.category.toLowerCase()} work: clean Mission Control, live workspace screens, agents, Decisions, Receipts, and permission paths around {demo.vocabulary.tracks}.
          </p>
        </div>
        <button
          onClick={() => onOpenScreen("Integrations")}
          className="inline-flex w-fit items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-semibold text-zinc-100 hover:bg-white/[0.07]"
        >
          <Link2 className="h-4 w-4" style={{ color: accent }} />
          Review data access
        </button>
      </div>

      <MissionSection label="Important setup" status={desktopPermissionGranted ? "Ready" : "Required"}>
        <div className="rounded-2xl border border-white/10 bg-[#101010] p-5 sm:p-7">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/[0.05]" style={{ color: accent }}>
                <Monitor className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Agent data permissions</h2>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-zinc-500">
                  Agents can improve this application once the user approves the source where {demo.vocabulary.tracks} already live. Nothing connects silently; access is reviewed first and logged as a Receipt.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {[
                    { title: "Ask where data lives", value: "Visible" },
                    { title: "Request smallest scope", value: desktopPermissionGranted ? "Granted" : "Waiting" },
                    { title: "Show connected source", value: desktopPermissionGranted ? "Ready" : "After approval" },
                  ].map((item) => (
                    <div key={item.title} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                      <div className="text-sm font-semibold">{item.title}</div>
                      <div className="mt-1 text-sm text-zinc-500">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <button
              onClick={() => onOpenScreen("Integrations")}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-[#090909]"
              style={{ backgroundColor: accent }}
            >
              Review setup path
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </MissionSection>

      <MissionSection label="Operating checklist" status={`${checklist.filter((item) => item.ok).length}/${checklist.length} ready`}>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {checklist.map((item) => (
            <button
              key={item.title}
              onClick={() => onOpenScreen(item.screen)}
              className="rounded-2xl border border-white/10 bg-[#101010] p-5 text-left transition-colors hover:bg-white/[0.05]"
            >
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                    item.ok ? "bg-emerald-500/10 text-emerald-400" : "bg-white/[0.05]"
                  )}
                  style={item.ok ? undefined : { color: accent }}
                >
                  {item.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                </div>
                <div>
                  <div className="font-semibold">{item.title}</div>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-500">{item.description}</p>
                </div>
              </div>
              <div className="mt-5 inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm font-semibold text-zinc-300">
                {item.action}
                <ChevronRight className="h-4 w-4" />
              </div>
            </button>
          ))}
        </div>
      </MissionSection>

      <div className="grid gap-8 xl:grid-cols-[1fr_0.96fr]">
        <MissionSection label="Decisions">
          <ActionCard
            title={desktopPermissionGranted ? "Desktop Agent permission active" : `Approve ${demo.category.toLowerCase()} source discovery`}
            description={desktopPermissionGranted
              ? `Agents can now read the approved demo source for ${demo.vocabulary.tracks}. The permission receipt is available in Receipts.`
              : `The Operator can ask where ${demo.vocabulary.tracks} live, then request only the permission needed before agents use that data.`}
            cta={desktopPermissionGranted ? "View integrations" : "Review permission"}
            onClick={() => onOpenScreen(desktopPermissionGranted ? "Integrations" : "Decisions")}
            accent={accent}
          />
        </MissionSection>

        <MissionSection label="The Pulse">
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricCard label="Tracked Records" value={String(records.length)} description={demo.vocabulary.tracks} accent={accent} />
            <MetricCard label="Stages" value={String(stages.length)} description={demo.vocabulary.stages} accent={accent} />
            <MetricCard label="Agents" value={String(agents.length)} description="active and recommended" accent={accent} />
          </div>
        </MissionSection>
      </div>

      <div className="grid gap-8 xl:grid-cols-[0.92fr_1.08fr]">
        <MissionSection label="Needs attention">
          <div className="rounded-2xl border border-white/10 bg-[#101010] p-5">
            <ActionCard
              title={desktopPermissionGranted ? "First source is connected" : `Live ${demo.vocabulary.tracks} source needs approval`}
              description={desktopPermissionGranted
                ? "The connected source is visible in Integrations and a permission receipt is available."
                : "The agent should ask where the source lives, request permission, then fill the workspace from approved data."}
              cta={desktopPermissionGranted ? "View receipt" : "Open Integrations"}
              onClick={() => onOpenScreen(desktopPermissionGranted ? "Receipts" : "Integrations")}
              accent={accent}
            />
          </div>
        </MissionSection>

        <MissionSection label="Staff board">
          <div className="space-y-3">
            {agents.map((agent) => (
              <button
                key={agent.name}
                onClick={() => onOpenScreen(agent.name)}
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left hover:bg-white/[0.06] transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">{agent.name}</div>
                    <div className="mt-1 text-sm text-zinc-500 line-clamp-2">{agent.summary}</div>
                  </div>
                  <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                    {agent.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </MissionSection>
      </div>

      <MissionSection label="Agent library">
        <div className="rounded-2xl border border-white/10 bg-[#101010] p-5">
          <div className="grid gap-5 xl:grid-cols-[1fr_1.2fr]">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/[0.05]" style={{ color: accent }}>
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold">Staff expansion</div>
                  <div className="text-sm text-zinc-500">{agents.filter((agent) => agent.status !== "Recommended").length} active now · {recommendedAgents.length} recommended next</div>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-zinc-500">
                Recommended agents are staged as Decisions first, so nothing joins the application without approval.
              </p>
              <button
                onClick={() => onOpenScreen("Agent Library")}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 px-4 py-3 text-sm font-semibold text-zinc-200 hover:bg-white/[0.05]"
              >
                View full agent library
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {recommendedAgents.slice(0, 2).map((agent) => (
                <button
                  key={agent.name}
                  onClick={() => onOpenScreen("Agent Library")}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-left transition-colors hover:bg-white/[0.06]"
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{demo.category}</div>
                  <div className="mt-3 font-semibold">{agent.name}</div>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-zinc-500">{agent.summary}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: accent }}>
                    View details
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </MissionSection>

      <MissionSection label="Receipts">
        <button
          onClick={() => onOpenScreen("Receipts")}
          className="w-full rounded-2xl border border-white/10 bg-[#101010] px-5 py-8 text-center text-sm text-zinc-500 transition-colors hover:bg-white/[0.04]"
        >
          {receiptsReady ? "Open recent Receipts — every agent move lands here." : "No moves yet — every action your staff takes lands here."}
        </button>
      </MissionSection>
    </div>
  );
}

function MissionSection({
  label,
  status,
  children,
}: {
  label: string;
  status?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</div>
        {status && (
          <div className="rounded-full bg-white/[0.06] px-3 py-1 text-[11px] font-bold text-zinc-400">
            {status}
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

function WorkspaceScreenView({
  demo,
  accent,
  activeScreen,
  stages,
  records,
  selectedRecord,
  setSelectedRecord,
  onTakeAction,
  singularNoun,
}: {
  demo: DemoData;
  accent: string;
  activeScreen: string;
  stages: string[];
  records: DemoShellRecord[];
  selectedRecord: DemoShellRecord | null;
  setSelectedRecord: (record: DemoShellRecord) => void;
  onTakeAction: (record: DemoShellRecord) => void;
  singularNoun: string;
}) {
  const isDashboard = /dashboard|analytics|performance|revenue|pulse|risk|quality/i.test(activeScreen);
  const isDocs = /document|vault|evidence|library|assets|photo/i.test(activeScreen);
  const isCalendar = /calendar|schedule|booking|shift|dispatch|run of show/i.test(activeScreen);

  return (
    <div className="space-y-6">
      <ScreenHeader demo={demo} screen={activeScreen} accent={accent} />

      {isDashboard ? (
        <div className="grid gap-5 lg:grid-cols-3">
          <MetricCard label="Work In Motion" value={String(records.length)} description={`${demo.vocabulary.tracks} visible`} accent={accent} />
          <MetricCard label="Attention Needed" value="2" description="source, approval, or stale follow-up" accent={accent} />
          <MetricCard label="Agent Outputs" value="4" description="tables, charts, summaries, next actions" accent={accent} />
          <Panel className="lg:col-span-2" title="Recommended Actions" icon={BarChart3} accent={accent}>
            <div className="space-y-3">
              {records.slice(0, 4).map((record) => (
                <RecordRow key={record.id} record={record} accent={accent} onClick={() => setSelectedRecord(record)} />
              ))}
            </div>
          </Panel>
          <RecordDetail record={selectedRecord} accent={accent} onTakeAction={onTakeAction} />
        </div>
      ) : isDocs ? (
        <div className="grid gap-5 lg:grid-cols-[1fr_0.75fr]">
          <Panel title={`${singularNoun} Documents`} icon={FolderOpen} accent={accent}>
            <div className="space-y-3">
              {["Source file", "Agent draft", "Approval note", "Final receipt"].map((label, index) => (
                <button key={label} className="w-full rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left hover:bg-white/[0.06]">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{label}</div>
                      <div className="mt-1 text-sm text-zinc-500">{records[index % records.length]?.title || demo.vocabulary.tracks}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-600" />
                  </div>
                </button>
              ))}
            </div>
          </Panel>
          <RecordDetail record={selectedRecord} accent={accent} onTakeAction={onTakeAction} />
        </div>
      ) : isCalendar ? (
        <Panel title={`${activeScreen} Timeline`} icon={Activity} accent={accent}>
          <div className="space-y-4">
            {records.map((record, index) => (
              <button key={record.id} onClick={() => setSelectedRecord(record)} className="flex w-full gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left hover:bg-white/[0.06]">
                <div className="rounded-lg px-3 py-2 text-center text-[#090909]" style={{ backgroundColor: accent }}>
                  <div className="text-[10px] font-bold uppercase">Day</div>
                  <div className="text-lg font-semibold">{index + 1}</div>
                </div>
                <div>
                  <div className="font-semibold">{record.title}</div>
                  <div className="mt-1 text-sm text-zinc-500">{record.nextAction}</div>
                </div>
              </button>
            ))}
          </div>
        </Panel>
      ) : (
        <div className="grid gap-5 xl:grid-cols-[1fr_320px]">
          <Panel title={`${singularNoun} Board`} icon={ClipboardList} accent={accent}>
            <div className="grid gap-4 lg:grid-cols-3">
              {stages.slice(0, 6).map((stage) => (
                <div key={stage} className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">{stage}</div>
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: accent }} />
                  </div>
                  <div className="space-y-3">
                    {records.filter((record) => record.stage === stage).slice(0, 2).map((record) => (
                      <button
                        key={record.id}
                        onClick={() => setSelectedRecord(record)}
                        className="w-full rounded-lg border border-white/10 bg-[#141414] p-3 text-left hover:border-white/20"
                      >
                        <div className="font-semibold">{record.title}</div>
                        <div className="mt-1 text-xs text-zinc-500">{record.owner}</div>
                        <div className="mt-3 text-xs font-semibold" style={{ color: accent }}>{record.signal}</div>
                      </button>
                    ))}
                    {!records.some((record) => record.stage === stage) && (
                      <div className="rounded-lg border border-dashed border-white/10 p-3 text-xs text-zinc-600">Waiting for live data</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
          <RecordDetail record={selectedRecord} accent={accent} onTakeAction={onTakeAction} />
        </div>
      )}
    </div>
  );
}

function AgentLibraryView({
  demo,
  accent,
  agents,
  onOpenScreen,
}: {
  demo: DemoData;
  accent: string;
  agents: ReturnType<typeof buildDemoShellAgents>;
  onOpenScreen: (screen: string) => void;
}) {
  const activeAgents = agents.filter((agent) => agent.status !== "Recommended");
  const recommendedAgents = agents.filter((agent) => agent.status === "Recommended");
  const specialist = recommendedAgents[0];

  return (
    <div className="space-y-6">
      <ScreenHeader demo={demo} screen="Agent Library" accent={accent} />
      <div className="grid gap-5 xl:grid-cols-[0.85fr_1.15fr]">
        <Panel title="Active Staff" icon={Bot} accent={accent}>
          <div className="space-y-3">
            {activeAgents.map((agent) => (
              <button
                key={agent.name}
                onClick={() => onOpenScreen(agent.name)}
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left hover:bg-white/[0.06]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold">{agent.name}</div>
                    <div className="mt-1 text-sm leading-relaxed text-zinc-500">{agent.summary}</div>
                  </div>
                  <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold uppercase" style={{ color: accent }}>
                    {agent.role}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Panel>

        <Panel title="Recommended Deployment" icon={Sparkles} accent={accent}>
          {specialist ? (
            <div className="grid gap-5 lg:grid-cols-[1fr_0.7fr]">
              <ActionCard
                title={specialist.name}
                description={specialist.summary}
                cta="Review deployment decision"
                accent={accent}
                onClick={() => onOpenScreen("Decisions")}
              />
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Why this appears</div>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  The application detected {demo.vocabulary.tracks}, {demo.vocabulary.stages}, and {demo.vocabulary.worksWith}. In production, unknown niches use research before suggestions appear here.
                </p>
                <button
                  onClick={() => onOpenScreen("Decisions")}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-[#090909]"
                  style={{ backgroundColor: accent }}
                >
                  Open Decisions
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-zinc-500">No specialist deployment recommended until research proves a clear need.</div>
          )}
        </Panel>
      </div>
    </div>
  );
}

function WeeklyReviewView({
  demo,
  accent,
  records,
  onOpenScreen,
}: {
  demo: DemoData;
  accent: string;
  records: DemoShellRecord[];
  onOpenScreen: (screen: string) => void;
}) {
  const stuck = records.filter((record) => record.signal === "Needs source");
  const ready = records.filter((record) => record.signal === "Agent ready");
  const queued = records.filter((record) => record.signal === "Decision queued");

  return (
    <div className="space-y-6">
      <ScreenHeader demo={demo} screen="Weekly Review" accent={accent} />
      <div className="grid gap-5 lg:grid-cols-3">
        <MetricCard label="Needs Source" value={String(stuck.length)} description={`missing data for ${demo.vocabulary.tracks}`} accent={accent} />
        <MetricCard label="Ready Moves" value={String(ready.length)} description="agent can move this work" accent={accent} />
        <MetricCard label="Needs Decision" value={String(queued.length)} description="approval before action" accent={accent} />
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_0.8fr]">
        <Panel title="This Week's Review Queue" icon={ClipboardList} accent={accent}>
          <div className="space-y-3">
            {records.slice(0, 5).map((record) => (
              <RecordRow key={record.id} record={record} accent={accent} onClick={() => onOpenScreen(record.signal === "Needs source" ? "Integrations" : "Receipts")} />
            ))}
          </div>
        </Panel>

        <Panel title="Agent Recommendations" icon={Sparkles} accent={accent}>
          <div className="space-y-3">
            <ActionCard
              title={`Connect the source for ${demo.vocabulary.tracks}`}
              description="The Operator should ask where the data lives, request the smallest permission, then show the connected source in Integrations."
              cta="Open Integrations"
              accent={accent}
              onClick={() => onOpenScreen("Integrations")}
            />
            <ActionCard
              title="Turn repeated updates into a screen"
              description={`The Architect watches repeated ${demo.category.toLowerCase()} work and proposes a durable workspace screen only after a clear pattern appears.`}
              cta="Open Decisions"
              accent={accent}
              onClick={() => onOpenScreen("Decisions")}
            />
          </div>
        </Panel>
      </div>
    </div>
  );
}

function IntegrationsView({
  demo,
  accent,
  permissionGranted,
  onGrantPermission,
  onOpenScreen,
}: {
  demo: DemoData;
  accent: string;
  permissionGranted: boolean;
  onGrantPermission: () => void;
  onOpenScreen: (screen: string) => void;
}) {
  const common = demo.playbook?.common_integrations || ["Email", "Spreadsheets", "Drive", "Desktop folders"];

  return (
    <div className="space-y-6">
      <ScreenHeader demo={demo} screen="Integrations" accent={accent} />
      <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
        <Panel title="Permission Path" icon={Monitor} accent={accent}>
          <ActionCard
            title={permissionGranted ? "Sloe Desktop Agent paired" : "Sloe Desktop Agent awaiting approval"}
            description={permissionGranted
              ? `Permission is active for the approved demo source. Agents can now use ${demo.vocabulary.tracks} in workspace screens and chat outputs.`
              : `In production, the application asks where ${demo.vocabulary.tracks} live, shows the exact source, then requests the smallest permission needed before agents read it.`}
            cta={permissionGranted ? "View permission receipt" : "Simulate Grant Permission"}
            accent={accent}
            onClick={permissionGranted ? () => onOpenScreen("Receipts") : onGrantPermission}
          />
        </Panel>
        <Panel title="Likely Sources" icon={Database} accent={accent}>
          <div className="grid gap-3 sm:grid-cols-2">
            {common.slice(0, 6).map((source, index) => {
              const connected = permissionGranted && index === 0;
              return (
                <div key={source} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{source}</div>
                    <span
                      className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold uppercase"
                      style={connected ? { color: accent } : undefined}
                    >
                      {connected ? "Connected" : "Needs approval"}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-zinc-500">Useful for {demo.vocabulary.tracks} and {demo.vocabulary.worksWith}.</div>
                  {connected && (
                    <div className="mt-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-xs text-zinc-400">
                      Receipt created. Agents now show this source as approved context.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function DecisionsView({
  demo,
  accent,
  selectedDecision,
  setSelectedDecision,
  onGrantPermission,
}: {
  demo: DemoData;
  accent: string;
  selectedDecision: string;
  setSelectedDecision: (id: string) => void;
  onGrantPermission: () => void;
}) {
  const decisions = [
    {
      id: "data_access",
      title: "Grant data access",
      description: `Let agents read the selected ${demo.vocabulary.tracks} source.`,
      detail: `The Operator will connect one approved source, attach it to Integrations, and log a receipt before any agent uses it.`,
      approval: "Requires user approval before data is read.",
      cta: "Approve demo permission",
    },
    {
      id: "specialist_agent",
      title: "Deploy specialist agent",
      description: `Add a ${demo.category.toLowerCase()} specialist after the application explains the need.`,
      detail: `The Architect would add a specialist agent only after the repeated work pattern is clear and the user accepts the deployment Decision.`,
      approval: "Would deploy only after approval in the live application.",
      cta: "Preview deployment path",
    },
    {
      id: "workspace_screen",
      title: "Create workspace screen",
      description: `Turn repeated ${demo.vocabulary.tracks} work into a permanent screen.`,
      detail: `The Architect would add the screen to the workspace shell, notify the owning agent, and record the update in Receipts.`,
      approval: "No screen is created until the Decision is approved.",
      cta: "Review screen path",
    },
  ];
  const active = decisions.find((decision) => decision.id === selectedDecision) || decisions[0];

  return (
    <div className="space-y-6">
      <ScreenHeader demo={demo} screen="Decisions" accent={accent} />
      <div className="grid gap-5 xl:grid-cols-[1fr_0.8fr]">
        <Panel title="Waiting For Approval" icon={ShieldCheck} accent={accent}>
          <div className="grid gap-4 lg:grid-cols-3 xl:grid-cols-1">
            {decisions.map((decision) => (
              <ActionCard
                key={decision.id}
                title={decision.title}
                description={decision.description}
                cta="View details"
                accent={accent}
                selected={decision.id === active.id}
                onClick={() => setSelectedDecision(decision.id)}
              />
            ))}
          </div>
        </Panel>

        <Panel title="Decision Detail" icon={ClipboardList} accent={accent}>
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold">{active.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-zinc-500">{active.detail}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Approval rule</div>
              <div className="mt-2 text-sm text-zinc-300">{active.approval}</div>
            </div>
            <button
              onClick={active.id === "data_access" ? onGrantPermission : undefined}
              className="inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-[#090909] disabled:cursor-not-allowed disabled:opacity-60"
              style={{ backgroundColor: accent }}
              disabled={active.id !== "data_access"}
            >
              {active.cta}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function ReceiptsView({ demo, accent, receipts }: { demo: DemoData; accent: string; receipts: DemoShellReceipt[] }) {
  return (
    <div className="space-y-6">
      <ScreenHeader demo={demo} screen="Receipts" accent={accent} />
      <Panel title="Agent Receipts" icon={ReceiptText} accent={accent}>
        <div className="space-y-3">
          {receipts.map((receipt) => (
            <div key={receipt.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold">{receipt.title}</div>
                  <div className="mt-1 text-sm text-zinc-500">{receipt.detail}</div>
                  <div className="mt-3 text-xs font-semibold text-zinc-500">{receipt.assistant}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold uppercase" style={{ color: accent }}>
                    {receipt.status}
                  </span>
                  <CheckCircle2 className="h-5 w-5" style={{ color: accent }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function AgentWorkspaceView({
  demo,
  accent,
  agentName,
  onOpenScreen,
}: {
  demo: DemoData;
  accent: string;
  agentName: string;
  onOpenScreen: (screen: string) => void;
}) {
  return (
    <div className="space-y-6">
      <ScreenHeader demo={demo} screen={agentName} accent={accent} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Workspace Data" icon={Database} accent={accent}>
          <div className="grid gap-3 sm:grid-cols-2">
            {["Data needed", "Outputs", "Failed runs", "Skills loaded"].map((item) => (
              <div key={item} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="font-semibold">{item}</div>
                <div className="mt-2 text-sm text-zinc-500">{screenDescription(item, demo)}</div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Useful Chat Output" icon={MessageSquare} accent={accent}>
          <div className="rounded-xl border border-white/10 bg-[#151515] p-4">
            <div className="text-sm text-zinc-500">When this agent answers, rich results can appear here and in chat.</div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {["Table", "Chart", "Action"].map((item) => (
                <div key={item} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">{item}</div>
                  <div className="mt-2 h-2 rounded-full bg-white/10" />
                  <div className="mt-2 h-2 w-2/3 rounded-full bg-white/10" />
                </div>
              ))}
            </div>
          </div>
          <button
            onClick={() => onOpenScreen("Integrations")}
            className="mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-[#090909]"
            style={{ backgroundColor: accent }}
          >
            Connect missing data
          </button>
        </Panel>
      </div>
    </div>
  );
}

function ScreenHeader({ demo, screen, accent }: { demo: DemoData; screen: string; accent: string }) {
  const Icon = screenIcon(screen);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
          <Icon className="h-4 w-4" style={{ color: accent }} />
          {demo.category} Agentic Application
        </div>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight">{screen}</h1>
        <p className="mt-2 max-w-2xl text-zinc-500">{screenDescription(screen, demo)}</p>
      </div>
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-zinc-400">
        <Bell className="h-4 w-4" style={{ color: accent }} />
        Demo data shown; live data connects after permission.
      </div>
    </div>
  );
}

function Panel({
  title,
  icon: Icon,
  accent,
  children,
  className,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  accent: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("rounded-2xl border border-white/10 bg-[#101010] p-5", className)}>
      <div className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        {title}
      </div>
      {children}
    </section>
  );
}

function ActionCard({
  title,
  description,
  cta,
  accent,
  selected = false,
  onClick,
}: {
  title: string;
  description: string;
  cta: string;
  accent: string;
  selected?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "group w-full rounded-xl border bg-white/[0.03] p-5 text-left transition-colors",
        selected ? "border-white/25 bg-white/[0.07]" : "border-white/10 hover:bg-white/[0.06]"
      )}
    >
      <div className="flex min-h-10 items-start justify-between gap-4">
        <div>
          <div className="font-semibold">{title}</div>
          <p className="mt-2 text-sm leading-relaxed text-zinc-500">{description}</p>
        </div>
        <Plus className="h-5 w-5 shrink-0 opacity-70 group-hover:opacity-100" style={{ color: accent }} />
      </div>
      <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: accent }}>
        {cta}
        <ChevronRight className="h-4 w-4" />
      </div>
    </button>
  );
}

function MetricCard({ label, value, description, accent }: { label: string; value: string; description: string; accent: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#101010] p-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-3 text-3xl font-semibold leading-none" style={{ color: accent }}>{value}</div>
      <div className="mt-3 text-sm text-zinc-500">{description}</div>
    </div>
  );
}

function RecordRow({ record, accent, onClick }: { record: DemoShellRecord; accent: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="w-full rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left hover:bg-white/[0.06]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold">{record.title}</div>
          <div className="mt-1 text-sm text-zinc-500">{record.nextAction}</div>
        </div>
        <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold uppercase" style={{ color: accent }}>{record.signal}</span>
      </div>
    </button>
  );
}

function RecordDetail({
  record,
  accent,
  onTakeAction,
}: {
  record: DemoShellRecord | null;
  accent: string;
  onTakeAction: (record: DemoShellRecord) => void;
}) {
  return (
    <Panel title="Expanded Card" icon={ClipboardList} accent={accent}>
      {record ? (
        <div className="space-y-4">
          <div>
            <div className="text-2xl font-semibold">{record.title}</div>
            <div className="mt-2 text-sm text-zinc-500">{record.owner} - {record.stage}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Next action</div>
            <div className="mt-2 text-sm text-zinc-300">{record.nextAction}</div>
          </div>
          <button
            onClick={() => onTakeAction(record)}
            className="inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-[#090909]"
            style={{ backgroundColor: accent }}
          >
            Take Action
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="text-sm text-zinc-500">Select a card to see the medium detail view before taking action.</div>
      )}
    </Panel>
  );
}
